/**
 * Formata número de telefone brasileiro.
 * - 10 dígitos (fixo): (XX) XXXX-XXXX
 * - 11 dígitos (celular): (XX) XXXXX-XXXX
 * Até o 8º dígito usa formato fixo; ao digitar o 9º, passa para celular.
 */
export function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  const len = digits.length;

  if (len <= 2) {
    return len ? `(${digits}` : '';
  }
  if (len <= 6) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  }
  if (len <= 10) {
    // Fixo: (XX) XXXX-XXXX
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  // Celular: (XX) XXXXX-XXXX
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

/** Retorna apenas os dígitos nacionais (10 ou 11) da parte formatada. */
export function getPhoneDigits(formatted: string): string {
  return formatted.replace(/\D/g, '').slice(0, 11);
}

/** Retorna o número com código do país para persistência (ex.: 55219877240). Parte nacional em (XX) XXXXX-XXXX. */
export function toStoredPhoneWithCountryCode(nationalFormatted: string): string {
  const digits = getPhoneDigits(nationalFormatted);
  return digits ? `+55${digits}` : '';
}

const BR_PREFIX = '+55 ';

/** Valor para exibir no input: prefixo +55 fixo + parte nacional formatada. */
export function formatPhoneWithCountryCode(nationalPart: string): string {
  return BR_PREFIX + nationalPart;
}

/**
 * A partir do valor do input (+55 (XX) XXXXX-XXXX), extrai e formata só a parte nacional.
 * Usado no onChange para manter no state apenas (XX) XXXXX-XXXX.
 */
export function parsePhoneInputToNational(inputValue: string): string {
  const digits = inputValue.replace(/\D/g, '');
  const national = digits.startsWith('55') ? digits.slice(2).slice(-11) : digits.slice(-11);
  return formatPhone(national);
}

/**
 * Converte número E.164 (ex.: +5511999999999) para formato de exibição brasileiro.
 * Se não for E.164 BR, retorna string vazia.
 */
export function formatE164ToDisplay(e164: string | null | undefined): string {
  if (!e164 || typeof e164 !== 'string') return '';
  const digits = e164.replace(/\D/g, '');
  if (digits.startsWith('55') && digits.length >= 12) {
    return formatPhone(digits.slice(2));
  }
  if (digits.length >= 10 && digits.length <= 11) return formatPhone(digits);
  return '';
}

/**
 * Normaliza valor vindo da API (com ou sem +55) para parte nacional formatada (XX) XXXXX-XXXX.
 * Evita exibir (55) como DDD quando o número já vem com código do país.
 */
export function normalizePhoneToNationalFormatted(phone: string | null | undefined): string {
  if (!phone || typeof phone !== 'string') return '';
  const digits = phone.replace(/\D/g, '');
  const national = digits.startsWith('55') ? digits.slice(2).slice(-11) : digits.slice(-11);
  return formatPhone(national);
}
